/**
 * InboundRepliesChip · Feb 2026 · Phase 1
 *
 * Tiny alert summary for the Advisor Dashboard / Today's Desk. Renders
 * nothing when there are no unread inbound replies. Clicking opens the
 * Concierge Desk with the Inbound Replies panel anchored open.
 *
 * Uses the existing design system — no new styling introduced.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";
import { Surface, api } from "../../index";

export default function InboundRepliesChip() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/inbox-alerts/summary")
      .then(({ data }) => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) setSummary(null); });
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const unread = summary?.unread || 0;
  if (unread === 0) return null;

  const label = unread === 1 ? "1 client reply needs review" : `${unread} client replies need review`;

  return (
    <Link
      to="/portal/advisor/concierge#inbound-replies"
      className="block group"
      data-testid="inbound-replies-chip"
    >
      <Surface
        variant="paper"
        padded
        className="!p-4 flex items-center gap-3 hover:bg-[hsl(var(--accent-gold))]/[0.05] transition-colors"
      >
        <span className="inline-flex items-center justify-center size-8 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] shrink-0">
          <Mail className="size-3.5" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
            Inbound Replies · Needs Attention
          </p>
          <p className="mt-0.5 font-serif text-[14px] text-[hsl(var(--foreground))] truncate">
            {label}
          </p>
        </div>
        <ArrowRight className="size-4 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--foreground))] transition-colors" strokeWidth={1.5} />
      </Surface>
    </Link>
  );
}
